
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import "moment/locale/ar";
import "./ServiceComments.scss";

const getCommentDate = (createdAt) => {
  if (!createdAt) return null;

  // Firestore Timestamp instance
  if (typeof createdAt.toDate === "function") {
    const date = createdAt.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Firestore Timestamp serialized as { seconds, nanoseconds }
  if (typeof createdAt.seconds === "number") {
    const date = new Date(
      createdAt.seconds * 1000 +
        (createdAt.nanoseconds || 0) / 1_000_000
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  // ISO date string, milliseconds, or another supported date value
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getInitials = (name) => {
  const cleanName = String(name || "").trim();

  if (!cleanName) return "؟";

  return cleanName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("");
};

const ServiceCommentsCard = ({ serviceComments }) => {
  const [showAll, setShowAll] = useState(false);

  const comments = useMemo(() => {
    // Support an array or a map/object containing comment objects.
    const source = Array.isArray(serviceComments)
      ? serviceComments
      : serviceComments && typeof serviceComments === "object"
      ? Object.values(serviceComments)
      : [];

    return source
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          typeof item.comment === "string" &&
          item.comment.trim().length > 0
      )
      .map((item, index) => ({
        ...item,
        _key: item.id || item.customer_id || `comment-${index}`,
        _date: getCommentDate(item.created_at),
      }))
      .sort(
        (a, b) =>
          (b._date?.getTime() || 0) -
          (a._date?.getTime() || 0)
      );
  }, [serviceComments]);

  const visibleComments = showAll
    ? comments
    : comments.slice(0, 3);

  return (
    <section className="service-comments-card" dir="rtl">
      <div className="service-comments-header">
        <div className="service-comments-heading">
          <div className="service-comments-heading-icon">
            <i className="bx bx-message-square-dots" />
          </div>

          <div>
            <h5>آراء العملاء</h5>
            <p>ماذا يقول العملاء عن هذه الخدمة؟</p>
          </div>
        </div>

        <span className="service-comments-count">
          <i className="bx bx-message-rounded" />
          {comments.length} تعليق
        </span>
      </div>

      {comments.length === 0 ? (
        <div className="service-comments-empty">
          <div className="service-comments-empty-icon">
            <i className="bx bx-conversation" />
          </div>
          <h6>لا توجد تعليقات حتى الآن</h6>
          <p>
            ستظهر آراء العملاء هنا عندما يشاركون تجربتهم مع هذه الخدمة.
          </p>
        </div>
      ) : (
        <>
          <div className="service-comments-list">
            {visibleComments.map((item) => (
              <article className="service-comment" key={item._key}>
                <div className="service-comment-avatar">
                  {getInitials(item.customer_name)}
                </div>

                <div className="service-comment-content">
                  <div className="service-comment-top">
                    <div className="service-comment-customer">
                      <h6>
                        {item.customer_name?.trim() || "عميل"}
                      </h6>

                      {item._date && (
                        <span className="service-comment-date">
                          <i className="bx bx-calendar" />
                          {moment(item._date)
                            .locale("ar")
                            .format("D MMMM YYYY، hh:mm A")}
                        </span>
                      )}
                    </div>

                    <i
                      className="bx bxs-quote-alt-left service-comment-quote"
                      aria-hidden="true"
                    />
                  </div>

                  <p className="service-comment-text">
                    {item.comment.trim()}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {comments.length > 3 && (
            <button
              type="button"
              className="service-comments-toggle"
              onClick={() => setShowAll((previous) => !previous)}
            >
              {showAll ? "عرض تعليقات أقل" : `عرض جميع التعليقات (${comments.length})`}
              <i
                className={`bx ${
                  showAll ? "bx-chevron-up" : "bx-chevron-down"
                }`}
              />
            </button>
          )}
        </>
      )}
    </section>
  );
};

ServiceCommentsCard.propTypes = {
  serviceComments: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
};

ServiceCommentsCard.defaultProps = {
  serviceComments: [],
};

export default ServiceCommentsCard;
